import { Theme } from '@logto/schemas';
import { useEffect, useContext } from 'react';

import { themeStorageKey } from '@/Providers/PageContextProvider';
import PageContext from '@/Providers/PageContextProvider/PageContext';

const prefersDarkSchemeQuery = '(prefers-color-scheme: dark)';

const getDarkThemeWatchMedia = (): MediaQueryList | undefined => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return undefined;
  }

  return window.matchMedia(prefersDarkSchemeQuery);
};

export const getThemeBySystemConfiguration = (): Theme =>
  getDarkThemeWatchMedia()?.matches ? Theme.Dark : Theme.Light;

export default function useTheme() {
  const { isPreview, experienceSettings, setTheme } = useContext(PageContext);

  useEffect(() => {
    if (!experienceSettings?.color.isDarkModeEnabled) {
      return;
    }

    // TDA: an explicit choice from the theme toggle outranks the OS setting.
    // Upstream follows `prefers-color-scheme` unconditionally, which overwrote
    // the user's pick on every mount and made the toggle look broken.
    const changeTheme = () => {
      const stored = localStorage.getItem(themeStorageKey);

      if (stored === Theme.Dark || stored === Theme.Light) {
        setTheme(stored);
        return;
      }

      setTheme(getThemeBySystemConfiguration());
    };

    changeTheme();

    const darkThemeWatchMedia = getDarkThemeWatchMedia();

    if (!darkThemeWatchMedia) {
      return;
    }

    darkThemeWatchMedia.addEventListener('change', changeTheme);

    return () => {
      darkThemeWatchMedia.removeEventListener('change', changeTheme);
    };
  }, [experienceSettings, isPreview, setTheme]);
}
